/**
 * MSAL Error Utility
 * Parses MSAL errors and returns user-friendly error messages
 */

/**
 * Parse MSAL error and return user-friendly message
 * @param error The error object from MSAL
 * @returns User-friendly error message
 */
export function parseMsalError(error: unknown): string {
  if (!(error instanceof Error)) {
    return 'An unexpected error occurred. Please try again.';
  }

  const errorMessage = error.message;
  const errorName = error.name || '';

  // User cancelled or declined consent
  if (
    errorMessage.includes('consent_required') ||
    errorMessage.includes('AADSTS65004') ||
    errorMessage.includes('User declined to consent')
  ) {
    return 'You need to accept the app permissions to sign in. Please try again and click "Accept" when prompted.';
  }

  // User closed the popup or cancelled
  if (
    errorMessage.includes('user_cancelled') ||
    errorMessage.includes('AADB2C90091') ||
    errorMessage.includes('popup_window_error') ||
    errorName === 'BrowserAuthError'
  ) {
    return 'Sign in was cancelled. Please try again.';
  }

  // Interaction required
  if (
    errorMessage.includes('interaction_required') ||
    errorMessage.includes('login_required')
  ) {
    return 'Please sign in to continue.';
  }

  // Network errors
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('CORS') ||
    errorMessage.includes('Failed to fetch')
  ) {
    return 'Network error. Please check your connection and try again.';
  }

  // Configuration errors
  if (
    errorMessage.includes('client_id') ||
    errorMessage.includes('authority') ||
    errorMessage.includes('redirect_uri')
  ) {
    return 'Authentication is not configured correctly. Please contact support.';
  }

  // Invalid grant errors
  if (
    errorMessage.includes('invalid_grant') ||
    errorMessage.includes('AADSTS50076') ||
    errorMessage.includes('AADSTS50079')
  ) {
    return 'Multi-factor authentication is required. Please complete the additional verification.';
  }

  // Token expired
  if (
    errorMessage.includes('token_expired') ||
    errorMessage.includes('AADSTS70043')
  ) {
    return 'Your session has expired. Please sign in again.';
  }

  // Account disabled or blocked
  if (
    errorMessage.includes('AADSTS50057') ||
    errorMessage.includes('account_locked')
  ) {
    return 'Your account has been disabled or locked. Please contact support.';
  }

  // External user not added to tenant (Google/other IdP users)
  if (
    errorMessage.includes('AADSTS16000') ||
    (errorMessage.includes('does not exist in tenant') &&
     errorMessage.includes('identity provider') &&
     errorMessage.includes('needs to be added as an external user'))
  ) {
    return 'Your account from an external identity provider (e.g., Google) is not yet added to this organization. Please contact your administrator to add your account as an external user in the tenant, or sign in with a different Azure Active Directory account.';
  }

  // Generic server error
  if (
    errorMessage.includes('server_error') ||
    errorMessage.includes('service_unavailable') ||
    errorMessage.includes('AADSTS50001')
  ) {
    return 'The authentication service is temporarily unavailable. Please try again later.';
  }

  // Return original error message if no specific match
  // Remove trace IDs and correlation IDs for cleaner display
  const cleanMessage = errorMessage
    .replace(/Trace ID: [a-f0-9-]+/gi, '')
    .replace(/Correlation ID: [a-f0-9-]+/gi, '')
    .replace(/Timestamp: [0-9-: TZ]+/gi, '')
    .trim();

  return cleanMessage || 'An error occurred during sign in. Please try again.';
}

/**
 * Check if error is a user cancellation (not a real error)
 * @param error The error object
 * @returns True if the error is just user cancellation
 */
export function isUserCancellation(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const errorMessage = error.message.toLowerCase();
  const errorName = error.name || '';

  return (
    errorMessage.includes('user_cancelled') ||
    errorMessage.includes('user_cancel') ||
    errorMessage.includes('user cancelled') ||
    errorMessage.includes('sign in was cancelled') ||
    errorMessage.includes('sign up was cancelled') ||
    errorMessage.includes('popup_window_error') ||
    errorMessage.includes('popup window error') ||
    errorName === 'BrowserAuthError'
  );
}

/**
 * Check if error requires admin consent
 * @param error The error object
 * @returns True if the error requires admin consent
 */
export function requiresAdminConsent(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const errorMessage = error.message;

  return (
    errorMessage.includes('AADSTS65001') ||
    errorMessage.includes('admin_consent_required') ||
    errorMessage.includes('need_admin_approval')
  );
}
